import { isArdoqError } from '@ardoq/common-helpers';
import {
  ConnectedComponentMessage,
  ConnectedReferenceMessage,
} from './ConnectedContentMessage';
import { bulkDeleteComponents } from 'services/modelService';
import { ArdoqId, ProvenanceSource } from '@ardoq/api-types';
import { componentApi, scenarioApi } from '@ardoq/api';
import { dispatchAction } from '@ardoq/rxbeach';
import { bulkComponentsDeleted } from 'streams/components/ComponentActions';
import { getActiveScenarioId } from 'streams/activeScenario/activeScenario$';
import { ModalSize } from '@ardoq/modal';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t, type I18nLocale } from '@ardoq/i18n';

export const deleteComponentsByIds = async (
  componentIds: ArdoqId[],
  provenanceSource?: ProvenanceSource
) => {
  const data = await bulkDeleteComponents(componentIds, provenanceSource);
  if (isArdoqError(data)) {
    return data;
  }

  if (data) {
    dispatchAction(bulkComponentsDeleted(data));
  }
  return data;
};

const getComponentTitleNoun = (
  count: number,
  i18nLocale: I18nLocale
): string =>
  count === 1
    ? t(i18nLocale, 'confirmDeletion:title.componentSingular', 'component')
    : t(i18nLocale, 'confirmDeletion:title.componentPlural', 'components');

const getReferenceTitleNoun = (
  count: number,
  i18nLocale: I18nLocale
): string =>
  count === 1
    ? t(i18nLocale, 'confirmDeletion:title.referenceSingular', 'reference')
    : t(i18nLocale, 'confirmDeletion:title.referencePlural', 'references');

export const getTitle = ({
  numOfComponents,
  numOfReferences,
  i18nLocale,
}: {
  numOfComponents: number;
  numOfReferences: number;
  i18nLocale: I18nLocale;
}): string => {
  if (numOfComponents && numOfReferences) {
    return t(
      i18nLocale,
      'confirmDeletion:title.deleteComponentsAndReferences',
      {
        numOfComponents,
        compNoun: getComponentTitleNoun(numOfComponents, i18nLocale),
        numOfReferences,
        refNoun: getReferenceTitleNoun(numOfReferences, i18nLocale),
      },
      'Delete {{numOfComponents}} {{compNoun}} and {{numOfReferences}} {{refNoun}}'
    );
  }
  if (numOfComponents) {
    return t(
      i18nLocale,
      'confirmDeletion:title.deleteComponents',
      {
        numOfComponents,
        compNoun: getComponentTitleNoun(numOfComponents, i18nLocale),
      },
      'Delete {{numOfComponents}} {{compNoun}}'
    );
  }
  if (numOfReferences) {
    return t(
      i18nLocale,
      'confirmDeletion:title.deleteReferences',
      {
        numOfReferences,
        refNoun: getReferenceTitleNoun(numOfReferences, i18nLocale),
      },
      'Delete {{numOfReferences}} {{refNoun}}'
    );
  }
  return t(i18nLocale, 'confirmDeletion:title.delete', 'Delete');
};

const getContent = ({
  componentIds,
  referenceIds,
  numOfReferences,
  numOfComponents,
  hasSubtreeInfo,
  componentNames,
  i18nLocale,
}: {
  componentIds: ArdoqId[];
  referenceIds: ArdoqId[];
  numOfComponents: number;
  numOfReferences: number;
  hasSubtreeInfo: boolean;
  componentNames: string[] | undefined;
  i18nLocale: I18nLocale;
}) =>
  numOfComponents ? (
    <ConnectedComponentMessage
      componentIds={componentIds}
      componentNames={componentNames}
      numOfReferences={numOfReferences}
      numOfComponents={numOfComponents}
      hasSubtreeInfo={hasSubtreeInfo}
      i18nLocale={i18nLocale}
    />
  ) : (
    <ConnectedReferenceMessage
      referenceIds={referenceIds}
      i18nLocale={i18nLocale}
    />
  );

export const getDialogConfigByIds = async (
  componentIds: ArdoqId[],
  referenceIds: ArdoqId[],
  componentNames?: string[]
) => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  const {
    numComponents: numOfComponents,
    numIncidentReferences: numOfIncidentReferences,
    hasSubtreeInfo,
  } = componentIds.length > 0
    ? await getTotalCountsOfCompsAndRefs(componentIds)
    : {
        numComponents: 0,
        numIncidentReferences: 0,
        hasSubtreeInfo: false,
      };

  const numOfReferences = numOfIncidentReferences + referenceIds.length;

  return {
    title: getTitle({
      numOfComponents,
      numOfReferences,
      i18nLocale,
    }),
    text: getContent({
      componentIds,
      referenceIds,
      numOfReferences,
      numOfComponents,
      hasSubtreeInfo,
      componentNames,
      i18nLocale,
    }),
    confirmButtonClickId: 'confirm-delete-components-references',
    modalSize: ModalSize.M,
  };
};

const getTotalCountsOfCompsAndRefs = async (componentIds: ArdoqId[]) => {
  const result = await getSubtreeInfoOfComponents(componentIds);
  if (isArdoqError(result)) {
    return {
      numComponents: componentIds.length,
      numIncidentReferences: 0,
      hasSubtreeInfo: false,
    };
  }
  return {
    ...result,
    hasSubtreeInfo: true,
  };
};

const getSubtreeInfoOfComponents = async (componentIds: ArdoqId[]) => {
  const scenarioId = getActiveScenarioId();
  if (!scenarioId) {
    return await componentApi.subtreeInfo(componentIds);
  }
  return await scenarioApi.subtreeInfo(scenarioId, componentIds);
};
